/**
 * Created by mariushirjanu on 12/06/2020.
 */
import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { IObject, GaStateDeclarationData } from '../../../type_definitions/datatypes';
import { TranslateService } from '@ngx-translate/core';
import { HttpDataService, IHttpDataCache } from '../http/http-data.service';
import { GaEventService } from '../services/ga-event.service';
import { EVENT_TRANSLATION_UPDATED } from '../app-events';
import { GaStateService, TranslationCategory } from '../services/ga-state.service';
import { Lang } from '../language/language-data.service';
import { GaDeferredService, IGADeferred } from '../services/ga-deferred.service';

export interface ITranslateOptions {
  translate?:boolean|ITranslateRouteParam;
  languages?:string[];
  modules?:TranslationCategory[];
}

export interface ITranslateRouteParam {
  routeParams: {
    [key:string]:ITranslateRouteConfig;
  };
}

interface ITranslateRouteConfig {
  translate?:true;
  languages?:Lang[];
}

@Injectable()
export class TranslationHelperService {
  private cache:IHttpDataCache<any>;
  private translationReady:IGADeferred<void>;

  constructor(
    private eventService:GaEventService,
    private translateService:TranslateService,
    private stateService:GaStateService,
    httpDataService:HttpDataService,
    deferredService:GaDeferredService
  ) {
    this.cache = httpDataService.createCache();
    this.translationReady = deferredService.defer();
  }

  public onReady():Promise<void> {
    // onReady flag is only blocking the rendering of the app until the first translation file is loaded
    // but is necessary to wait until the correct language has been decided and set in the UI
    return this.translationReady.promise;
  }

  public setReady():void {
    this.translationReady.resolve();
  }

  // this loads translations which are specified in the data object of the ui route state
  // e.g. data: { translate: true, languages: ['de'], modules: ['orderentry', 'webshop'] }, -> loads orderentry and webshop translations and allow to translate the screen only in Germany
  public async loadTranslations(stateData:GaStateDeclarationData, loadCurrentLang?:boolean):Promise<any> {
    if (!_.isEmpty(stateData?.modules)) {
      const defaultLangRequests:Promise<Object>[] = [];
      const currentLangRequests:Promise<Object>[] = [];
      _.each(stateData.modules, (moduleName:string) => {
        defaultLangRequests.push(this.cache.doGET(`rest/i18n/${moduleName}/${this.translateService.defaultLang}`));

        if (this.isTranslatable(stateData, loadCurrentLang)) {
          currentLangRequests.push(this.cache.doGET(`rest/i18n/${moduleName}/${this.translateService.currentLang}`));
        }
      });

      const loadTranslationsTable = (requests:Promise<Object>[], lang:string):Promise<IObject> => {
        return Promise.all(requests)
          .then((i18n:Object[]) => {
            let translationTable:IObject = {};
            _.each(i18n, (translations:IObject) => {
              translationTable = _.extend(translationTable, translations);
            });
            this.translateService.setTranslation(lang, translationTable, true);
            return translationTable;
          });
      };

      return Promise.all([
        loadTranslationsTable(defaultLangRequests, this.translateService.defaultLang),
        loadTranslationsTable(currentLangRequests, this.translateService.currentLang)
      ]).then(() => {
        this.eventService.sendMessage(EVENT_TRANSLATION_UPDATED, this.translateService.translations); // send translations to the loader
      });
    }

    return Promise.resolve();
  }

  private isTranslatable(stateData:GaStateDeclarationData, loadCurrentLang?:boolean):boolean {
    if (this.translateService.currentLang !== this.translateService.getDefaultLang()) {
      // if 'loadCurrentLang' is defined then return true because we want to load modules in current language as well if it's different from default language (eng)
      if (loadCurrentLang) {
        return true;
      }

      if (_.isBoolean(stateData?.translate)) {
        return stateData.translate && (!stateData?.languages || _.contains(stateData.languages, this.translateService.currentLang));
      } else {
        if (stateData?.translate?.routeParams) {
          const routeConfig:ITranslateRouteConfig = stateData.translate.routeParams[ this.stateService?.currentView ];
          return routeConfig?.translate && (!routeConfig?.languages || _.contains(routeConfig.languages, this.translateService.currentLang));
        }
      }
    }
  }
}